import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getEmployeeList } from "../../services/apiService";
import { authFetch } from "../../services/authService";
import { supabase } from "../../supabaseClient";
import LoadingButton from "../LoadingButton/LoadingButton";
import { OrbitLoaderComponent } from "../../utils/orbitLoader";
import {
  generateRandomPassword,
  validatePassword,
} from "../../utils/authUtils";
import "./PersonnelList.css";
// Türkçe gün adları ve renkler
const turkishDays = {
  Mon: "Pzt",
  Tue: "Sal",
  Wed: "Çar",
  Thu: "Per",
  Fri: "Cum",
  Sat: "Cmt",
  Sun: "Paz",
};
const API_BASE_URL = "https://alternative-lamprey-fatma-celik-a9c7fc96.koyeb.app";
const dayColors = {
  Pzt: "#FF6B6B", // Pazartesi - Kırmızı
  Sal: "#4ECDC4", // Salı - Turkuaz
  Çar: "#FFD166", // Çarşamba - Sarı
  Per: "#118AB2", // Perşembe - Mavi
  Cum: "#073B4C", // Cuma - Lacivert
  Cmt: "#6A4C93", // Cumartesi - Mor
  Paz: "#F15BB5", // Pazar - Pembe
};
// Günlerin renkli daire içinde gösterimi için bileşen
const DayCircle = ({ day, size = "default" }) => {
  const turkishDay = turkishDays[day] || day;
  const color = dayColors[turkishDay] || "#CCCCCC";
  const circleSize = size === "small" ? 20 : 30;
  return (
    <div
      className="day-circle"
      style={{
        backgroundColor: color,
        width: circleSize,
        height: circleSize,
        lineHeight: circleSize + "px",
        fontSize: size === "small" ? 10 : 14,
      }}
    >
      {turkishDay}
    </div>
  );
};
const PersonnelList = ({ onUserScheduleClick }) => {
  const navigate = useNavigate();
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    email: "",
    phone: "",
    rfid_tag: "",
    role_id: 2,
    password: "", // Sadece edit modalı için kullanılacak
  });
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [passwordTouched, setPasswordTouched] = useState(false);
  useEffect(() => {
    fetchPersonnel();
    const handlePersonnelSettingsUpdated = (event) => {
      console.log("Personel ayarları güncellendi, liste yenileniyor...");
      fetchPersonnel();
      if (showDetailsModal && selectedPerson) {
        const { userId, allUsers } = event.detail;
        if (allUsers || userId === selectedPerson.user_id) {
          const fetchUpdatedPerson = async () => {
            try {
              const response = await authFetch(
                `${API_BASE_URL}/api/employees/${selectedPerson.user_id}`
              );
              const data = await response.json();
              if (response.ok) {
                setSelectedPerson(data);
              }
            } catch (error) {
              console.error("Personel verileri güncellenemedi:", error);
            }
          };
          fetchUpdatedPerson();
        }
      }
    };
    const handleWorkScheduleUpdated = (event) => {
      console.log("Mesai ayarları güncellendi, liste yenileniyor...");
      fetchPersonnel();
      if (showDetailsModal && selectedPerson) {
        const { userId } = event.detail;
        if (userId === selectedPerson.user_id) {
          const fetchUpdatedPerson = async () => {
            try {
              const response = await authFetch(
                `${API_BASE_URL}/api/employees/${selectedPerson.user_id}`
              );
              const data = await response.json();
              if (response.ok) {
                setSelectedPerson(data);
              }
            } catch (error) {
              console.error("Personel verileri güncellenemedi:", error);
            }
          };
          fetchUpdatedPerson();
        }
      }
    };
    window.addEventListener(
      "personnelSettingsUpdated",
      handlePersonnelSettingsUpdated
    );
    window.addEventListener("workScheduleUpdated", handleWorkScheduleUpdated);
    return () => {
      window.removeEventListener(
        "personnelSettingsUpdated",
        handlePersonnelSettingsUpdated
      );
      window.removeEventListener(
        "workScheduleUpdated",
        handleWorkScheduleUpdated
      );
    };
  }, [showDetailsModal, selectedPerson]);
  useEffect(() => {
    fetchPersonnel();
  }, [showInactive]);
  const fetchPersonnel = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_BASE_URL}/api/employees`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Personel listesi yüklenemedi");
      }
      const filteredPersonnel = data.filter((person) =>
        showInactive ? !person.is_active : person.is_active
      );
      const personnelWithSchedules = await Promise.all(
        filteredPersonnel.map(async (person) => {
          try {
            const { data: workSchedules, error } = await supabase
              .from("work_schedules")
              .select("*")
              .eq("user_id", person.user_id)
              .order("date", { ascending: false })
              .limit(1);
            if (!error && workSchedules && workSchedules.length > 0) {
              return {
                ...person,
                work_schedule: workSchedules[0],
              };
            }
            return person;
          } catch (err) {
            console.error("Error fetching work schedule:", err);
            return person;
          }
        })
      );
      setPersonnel(personnelWithSchedules);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  const handleRowClick = (person) => {
    setSelectedPerson(person);
    setShowDetailsModal(true);
  };
  const handleAction = (type, person = null) => {
    setError("");
    setSuccess("");
    setModalType(type);
    setSelectedPerson(person);
    setPasswordErrors([]);
    setPasswordTouched(false);
    if (person) {
      setFormData({
        name: person.name,
        surname: person.surname,
        email: person.email,
        phone: person.phone || "",
        rfid_tag: person.rfid_tag,
        role_id: person.role_id,
        password: "", // Edit modalı için şifre boş başlar
      });
    } else {
      setFormData({
        name: "",
        surname: "",
        email: "",
        phone: "",
        rfid_tag: "",
        role_id: 2,
        password: "", // Add modalı için şifre boş
      });
    }
    setShowModal(true);
  };
  const closeModal = () => {
    setShowModal(false);
    setModalType("");
    setSelectedPerson(null);
    setFormLoading(false);
    setError("");
    setSuccess("");
    setPasswordErrors([]);
    setPasswordTouched(false);
  };
  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedPerson(null);
  };
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (name === "password") {
      setPasswordTouched(true);
      const validation = validatePassword(value);
      if (!validation.isValid) {
        setPasswordErrors([validation.message]);
      } else {
        setPasswordErrors([]);
      }
    }
  };
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (modalType === "edit" && formData.password) {
      const validation = validatePassword(formData.password);
      if (!validation.isValid) {
        setError(validation.message);
        return;
      }
    }
    setFormLoading(true);
    setError("");
    setSuccess("");
    try {
      let response;
      let result;
      if (modalType === "add") {
        // Artık her zaman rastgele şifre oluşturulacak
        response = await authFetch(`${API_BASE_URL}/api/employees`, {
          method: "POST",
          body: JSON.stringify({
            ...formData,
            // Backend otomatik şifre oluşturacak
          }),
        });
        result = await response.json();
        if (!response.ok) {
          throw new Error(result.error || "Personel eklenemedi");
        }
        const tempPassword =
          result.data?.temp_password ||
          "Oluşturulan şifre e-posta ile gönderildi";
        setSuccess(
          `${
            result.message || "Personel başarıyla eklendi"
          }. Rastgele şifre oluşturuldu ve e-posta ile gönderildi.`
        );
      } else if (modalType === "edit") {
        const updateData = { ...formData };
        if (updateData.password) {
          // Şifreyi doğrudan gönder, backend hash'leyecek
        } else {
          delete updateData.password;
        }
        response = await authFetch(
          `${API_BASE_URL}/api/employees/${selectedPerson.user_id}`,
          {
            method: "PUT",
            body: JSON.stringify(updateData),
          }
        );
        result = await response.json();
        if (!response.ok) {
          throw new Error(result.error || "Personel güncellenemedi");
        }
        setSuccess(result.message || "Personel başarıyla güncellendi");
      } else if (modalType === "toggle") {
        response = await authFetch(
          `${API_BASE_URL}/api/employees/${selectedPerson.user_id}/toggle-status`,
          {
            method: "PUT",
          }
        );
        result = await response.json();
        if (!response.ok) {
          throw new Error(result.error || "Durum değiştirilemedi");
        }
        if (result.success) {
          setSuccess(result.message);
        } else {
          throw new Error(result.error || "Durum değiştirilemedi");
        }
      }
      setTimeout(() => {
        fetchPersonnel();
        closeModal();
      }, 1500);
    } catch (error) {
      setError(error.message || "Bir hata oluştu");
    } finally {
      setFormLoading(false);
    }
  };
  const handleScheduleClick = (userId) => {
    console.log("handleScheduleClick called with userId:", userId);
    const employee = personnel.find((p) => p.user_id === userId);
    if (employee && employee.work_schedule) {
      setSelectedSchedule(employee.work_schedule);
      setShowScheduleModal(true);
    } else {
      navigate("/admin/settings", { state: { userId, workSchedule: true } });
    }
  };
  const handlePermissionsClick = (userId) => {
    console.log("handlePermissionsClick called with userId:", userId);
    navigate("/admin/permissions", { state: { userId } });
  };
  const getRoleName = (person) => {
    if (person.role_name) {
      const role = person.role_name.toLowerCase();
      if (role === "admin") return "Admin";
      if (role === "maintenance_admin") return "Bakım Personeli";
      return "Personel";
    }
    if (person.roles && typeof person.roles === "object") {
      if (person.roles.role_name) {
        const role = person.roles.role_name.toLowerCase();
        if (role === "admin") return "Admin";
        if (role === "maintenance_admin") return "Bakım Personeli";
        return "Personel";
      }
      if (Array.isArray(person.roles) && person.roles.length > 0) {
        const role = person.roles[0].role_name.toLowerCase();
        if (role === "admin") return "Admin";
        if (role === "maintenance_admin") return "Bakım Personeli";
        return "Personel";
      }
    }
    if (person.role_id !== undefined && person.role_id !== null) {
      const roleId =
        typeof person.role_id === "string"
          ? person.role_id.toLowerCase()
          : person.role_id;
      if (roleId === 1 || roleId === "1" || roleId === "admin") {
        return "Admin";
      }
      if (roleId === 3 || roleId === "3" || roleId === "maintenance_admin") {
        return "Bakım Personeli";
      }
      return "Personel";
    }
    return "Personel";
  };
  const getRoleBadge = (person) => {
    const roleName = getRoleName(person);
    return (
      <span
        className={`role-badge ${
          roleName === "Admin"
            ? "admin"
            : roleName === "Bakım Personeli"
            ? "maintenance"
            : "personel"
        }`}
      >
        {roleName}
      </span>
    );
  };
  const getStatusBadge = (isActive) => {
    return (
      <span className={`status-badge ${isActive ? "active" : "inactive"}`}>
        {isActive ? "Aktif" : "Pasif"}
      </span>
    );
  };
  const formatPhoneNumber = (phone) => {
    if (!phone) return "-";
    if (phone.startsWith("0")) {
      return phone.substring(1);
    }
    return phone;
  };
  const getWorkHoursDisplay = (person) => {
    if (person.work_schedule) {
      const { start_time, end_time } = person.work_schedule;
      return `${start_time.substring(0, 5)} - ${end_time.substring(0, 5)}`;
    }
    return "09:00 - 18:00";
  };
  const getWorkDaysDisplay = (person) => {
    if (person.work_schedule && person.work_schedule.days) {
      return person.work_schedule.days;
    }
    return ["Mon", "Tue", "Wed", "Thu", "Fri"];
  };
  if (loading) {
    return (
      <div className="loading-container">
        <OrbitLoaderComponent size="medium" showPath={true} />
        <p style={{ transform: "none" }}>Yükleniyor...</p>
      </div>
    );
  }
  return (
    <div className="personnel-list">
      <div className="list-header">
        <h2>Personel Yönetimi</h2>
        <div className="header-actions">
          <LoadingButton
            className="btn btn-primary"
            onClick={() => handleAction("add")}
          >
            <i className="fas fa-plus"></i> Yeni Personel Ekle
          </LoadingButton>
          <button
            className={`btn ${!showInactive ? "btn-success" : "btn-secondary"}`}
            onClick={() => setShowInactive(false)}
          >
            <i className="fas fa-user-check"></i> Aktif Personeller
          </button>
          <button
            className={`btn ${showInactive ? "btn-danger" : "btn-secondary"}`}
            onClick={() => setShowInactive(true)}
          >
            <i className="fas fa-user-times"></i> Pasif Personeller
          </button>
        </div>
      </div>
      <div className="table-container">
        <table className="personnel-table">
          <thead>
            <tr>
              <th>
                <i className="fas fa-user" style={{ marginRight: "8px" }}></i>Ad
                Soyad
              </th>
              <th>
                <i
                  className="fas fa-envelope"
                  style={{ marginRight: "8px" }}
                ></i>
                E-posta
              </th>
              <th>
                <i className="fas fa-phone" style={{ marginRight: "8px" }}></i>
                Telefon
              </th>
              <th>
                <i
                  className="fas fa-user-tag"
                  style={{ marginRight: "8px" }}
                ></i>
                Rol
              </th>
              <th>
                <i className="fas fa-cogs" style={{ marginRight: "8px" }}></i>
                İşlemler
              </th>
            </tr>
          </thead>
          <tbody>
            {personnel.length > 0 ? (
              personnel.map((person) => (
                <tr
                  key={person.user_id}
                  className="clickable-row"
                  onClick={() => handleRowClick(person)}
                >
                  <td>
                    <strong>
                      {person.name} {person.surname}
                    </strong>
                  </td>
                  <td>{person.email}</td>
                  <td>{formatPhoneNumber(person.phone)}</td>
                  <td>{getRoleBadge(person)}</td>
                  <td className="actions" onClick={(e) => e.stopPropagation()}>
                    {!showInactive && (
                      <>
                        <button
                          className="btn-icon btn-schedule"
                          onClick={() => handleScheduleClick(person.user_id)}
                          title="Mesai Ayarları"
                        >
                          <i className="fas fa-calendar-alt"></i>
                        </button>
                        <button
                          className="btn-icon btn-permissions"
                          onClick={() => handlePermissionsClick(person.user_id)}
                          title="İzinler"
                        >
                          <i className="fas fa-user-shield"></i>
                        </button>
                      </>
                    )}
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleAction("edit", person)}
                      title="Düzenle"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className={`btn-icon ${
                        person.is_active ? "btn-deactivate" : "btn-activate"
                      }`}
                      onClick={() => handleAction("toggle", person)}
                      title={person.is_active ? "Pasif Yap" : "Aktif Et"}
                    >
                      <i
                        className={`fas ${
                          person.is_active ? "fa-user-times" : "fa-user-check"
                        }`}
                      ></i>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="no-data">
                  <i className="fas fa-users-slash"></i>
                  <p>
                    {showInactive
                      ? "Pasif personel bulunamadı"
                      : "Aktif personel bulunamadı"}
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {/* Personel Detay Modal */}
      {showDetailsModal && selectedPerson && (
        <div className="modal-overlay">
          <div className="modal-content details-modal">
            <div className="modal-header">
              <h3>
                <i
                  className="fas fa-user-circle"
                  style={{ marginRight: "10px" }}
                ></i>
                Personel Detayları
              </h3>
              <button onClick={closeDetailsModal} className="close-button">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="person-details">
                <div className="detail-row">
                  <label>
                    <i className="fas fa-user"></i> Ad Soyad:
                  </label>
                  <span>
                    {selectedPerson.name} {selectedPerson.surname}
                  </span>
                </div>
                <div className="detail-row">
                  <label>
                    <i className="fas fa-envelope"></i> E-posta:
                  </label>
                  <span>{selectedPerson.email}</span>
                </div>
                <div className="detail-row">
                  <label>
                    <i className="fas fa-phone"></i> Telefon:
                  </label>
                  <span>{formatPhoneNumber(selectedPerson.phone)}</span>
                </div>
                <div className="detail-row">
                  <label>
                    <i className="fas fa-id-card"></i> RFID Kart:
                  </label>
                  <span>{selectedPerson.rfid_tag || "-"}</span>
                </div>
                <div className="detail-row">
                  <label>
                    <i className="fas fa-user-tag"></i> Rol:
                  </label>
                  {getRoleBadge(selectedPerson)}
                </div>
                <div className="detail-row">
                  <label>
                    <i className="fas fa-clock"></i> Çalışma Saatleri:
                  </label>
                  <span>{getWorkHoursDisplay(selectedPerson)}</span>
                </div>
                <div className="detail-row">
                  <label>
                    <i className="fas fa-calendar-alt"></i> Çalışma Günleri:
                  </label>
                  <div className="work-days">
                    {getWorkDaysDisplay(selectedPerson).map((day, index) => (
                      <DayCircle key={index} day={day} />
                    ))}
                  </div>
                </div>
                <div className="detail-row">
                  <label>
                    <i className="fas fa-info-circle"></i> Durum:
                  </label>
                  {getStatusBadge(selectedPerson.is_active)}
                </div>
              </div>
              <div className="details-actions">
                {!showInactive && (
                  <>
                    <button
                      className="btn btn-primary btn-small"
                      onClick={() => {
                        closeDetailsModal();
                        handleScheduleClick(selectedPerson.user_id);
                      }}
                    >
                      <i className="fas fa-calendar-alt"></i> Mesai Ayarları
                    </button>
                    <button
                      className="btn btn-primary btn-small"
                      onClick={() => {
                        closeDetailsModal();
                        handlePermissionsClick(selectedPerson.user_id);
                      }}
                    >
                      <i className="fas fa-user-shield"></i> İzinler
                    </button>
                  </>
                )}
                <button
                  className="btn btn-primary btn-small"
                  onClick={() => {
                    closeDetailsModal();
                    handleAction("edit", selectedPerson);
                  }}
                >
                  <i className="fas fa-edit"></i> Düzenle
                </button>
                <button
                  className={`btn btn-small ${
                    selectedPerson.is_active ? "btn-danger" : "btn-success"
                  }`}
                  onClick={() => {
                    closeDetailsModal();
                    handleAction("toggle", selectedPerson);
                  }}
                >
                  <i
                    className={`fas ${
                      selectedPerson.is_active
                        ? "fa-user-times"
                        : "fa-user-check"
                    }`}
                  ></i>
                  {selectedPerson.is_active ? "Pasif Yap" : "Aktif Et"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Mesai Planı Modal */}
      {showScheduleModal && selectedSchedule && (
        <div className="modal-overlay">
          <div className="modal-content schedule-modal">
            <div className="modal-header">
              <h3>
                <i
                  className="fas fa-calendar-alt"
                  style={{ marginRight: "10px" }}
                ></i>
                Mesai Takvimi
              </h3>
              <button
                onClick={() => setShowScheduleModal(false)}
                className="close-button"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="schedule-details">
                <div className="schedule-section">
                  <h4>Çalışma Saatleri</h4>
                  <div className="schedule-time">
                    <i className="fas fa-clock"></i>
                    {getWorkHoursDisplay(selectedSchedule)}
                  </div>
                </div>
                <div className="schedule-section">
                  <h4>Çalışma Günleri</h4>
                  <div className="days-container">
                    {getWorkDaysDisplay(selectedSchedule).map((day, index) => (
                      <DayCircle key={index} day={day} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowScheduleModal(false)}
              >
                Kapat
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowScheduleModal(false);
                  navigate("/admin/settings", {
                    state: {
                      userId: selectedPerson?.user_id,
                      workSchedule: true,
                    },
                  });
                }}
              >
                <i className="fas fa-edit"></i> Düzenle
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Form Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                {modalType === "add" && "Yeni Personel Ekle"}
                {modalType === "edit" && "Personel Düzenle"}
                {modalType === "toggle" &&
                  (selectedPerson?.is_active
                    ? "Personeli Pasif Yap"
                    : "Personeli Aktif Et")}
              </h3>
              <button onClick={closeModal} className="close-button">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Ad</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Soyad</label>
                    <input
                      type="text"
                      name="surname"
                      value={formData.surname}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>E-posta</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Telefon</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleFormChange}
                      placeholder="5551112233"
                      required
                    />
                    <small className="form-text">
                      Telefon numarasını başında 0 olmadan girin (ör:
                      5551112233)
                    </small>
                  </div>
                </div>
                {modalType === "add" && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>RFID Kart No</label>
                      <input
                        type="text"
                        name="rfid_tag"
                        value={formData.rfid_tag}
                        onChange={handleFormChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Rol</label>
                      <select
                        name="role_id"
                        value={formData.role_id}
                        onChange={handleFormChange}
                      >
                        <option value={1}>Admin</option>
                        <option value={2}>Personel</option>
                        <option value={3}>Bakım Personeli</option>
                      </select>
                    </div>
                  </div>
                )}
                {modalType === "edit" && (
                  <>
                    <div className="form-group">
                      <label>RFID Kart No</label>
                      <input
                        type="text"
                        name="rfid_tag"
                        value={formData.rfid_tag}
                        onChange={handleFormChange}
                        required
                      />
                      <small className="form-text">
                        Personelin RFID kart numarasını güncelleyebilirsiniz
                      </small>
                    </div>
                    <div className="form-group">
                      <label>Yeni Şifre (İsteğe Bağlı)</label>
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleFormChange}
                        placeholder="Sadece değiştirmek isterseniz doldurun"
                        className={
                          passwordErrors.length > 0 ? "input-error" : ""
                        }
                      />
                      <small className="form-text">
                        Boş bırakırsanız mevcut şifre korunur
                      </small>
                      {formData.password && (
                        <div className="password-requirements">
                          <p>Şifre şu gereksinimleri sağlamalıdır:</p>
                          <ul>
                            <li
                              className={
                                formData.password.length >= 8
                                  ? "requirement-met"
                                  : ""
                              }
                            >
                              En az 8 karakter
                            </li>
                            <li
                              className={
                                /[A-Z]/.test(formData.password)
                                  ? "requirement-met"
                                  : ""
                              }
                            >
                              En az bir büyük harf (A-Z)
                            </li>
                            <li
                              className={
                                /[a-z]/.test(formData.password)
                                  ? "requirement-met"
                                  : ""
                              }
                            >
                              En az bir küçük harf (a-z)
                            </li>
                            <li
                              className={
                                /[0-9]/.test(formData.password)
                                  ? "requirement-met"
                                  : ""
                              }
                            >
                              En az bir rakam (0-9)
                            </li>
                            <li
                              className={
                                /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(
                                  formData.password
                                )
                                  ? "requirement-met"
                                  : ""
                              }
                            >
                              En az bir özel karakter (!@#$%^&* vb.)
                            </li>
                          </ul>
                        </div>
                      )}
                      {passwordTouched && passwordErrors.length > 0 && (
                        <div className="password-errors">
                          {passwordErrors.map((error, index) => (
                            <div key={index} className="error-text">
                              {error}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Rol</label>
                      <select
                        name="role_id"
                        value={formData.role_id}
                        onChange={handleFormChange}
                      >
                        <option value={1}>Admin</option>
                        <option value={2}>Personel</option>
                        <option value={3}>Bakım Personeli</option>
                      </select>
                    </div>
                  </>
                )}
                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn btn-secondary"
                    style={{ minWidth: "100px" }}
                  >
                    İptal
                  </button>
                  <LoadingButton
                    type="submit"
                    className={`btn ${
                      modalType === "toggle"
                        ? selectedPerson?.is_active
                          ? "btn-danger"
                          : "btn-success"
                        : "btn-primary"
                    }`}
                    isLoading={formLoading}
                    style={{ minWidth: "120px" }}
                  >
                    {modalType === "add" && "Ekle"}
                    {modalType === "edit" && "Güncelle"}
                    {modalType === "toggle" &&
                      (selectedPerson?.is_active ? "Pasif Yap" : "Aktif Et")}
                  </LoadingButton>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default PersonnelList;
